import type { ProfileType, UserRole, UserStatus } from "@/lib/types";

/**
 * Minimal client-side memory of who is signed in. The API is cookie-based and
 * exposes no GET /me, so we persist the non-sensitive identity returned by
 * /auth/login to drive UI affordances (match overlay, browse CTAs). This is
 * not an auth source of truth — the server cookies are.
 */
const KEY = "pf_me";

export interface StoredUser {
  username: string;
  profileType: ProfileType;
  /** Absent for sessions stored before roles were tracked → treat as "user". */
  role?: UserRole;
  /**
   * Account lifecycle at sign-in. Drives client gating: `pending` users are
   * bounced to the waiting room (/pending). Absent for sessions stored before
   * status was tracked → treated as active (no bounce).
   */
  status?: UserStatus;
}

/** True when the stored user is staff (moderator or admin). */
export function isStaff(user: StoredUser | null): boolean {
  return user?.role === "moderator" || user?.role === "admin";
}

export function saveMe(user: StoredUser) {
  try {
    localStorage.setItem(KEY, JSON.stringify(user));
  } catch {
    /* ignore storage failures */
  }
}

export function getMe(): StoredUser | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as StoredUser) : null;
  } catch {
    return null;
  }
}

export function clearMe() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
